import { Badge, Button, Card, Modal, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Shield } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { FlowResponse } from "@/api";
import { authorizeFlowMutation, errorMessage, getFlowAuthorizationOptions, getFlowAuthorizationQueryKey } from "@/api";
import { Text } from "@/components/Text";
import { useCapabilities } from "@/features/account/useCurrentUser";

/**
 * Background-access authorization for scheduled flows (ported from the old
 * UI's ScheduledFlowAuthControl). A scheduled flow can call delegated tools
 * (email, SharePoint, Teams) on the user's behalf when it runs unattended; that
 * needs an explicit consent that captures the user's delegated token.
 *
 * Backend-gated: shown only when delegated-token caching is on org-wide (the
 * `automatedSyncEnabled` capability) and the flow has a scheduled trigger. The
 * status is user-scoped (one authorization covers all of the user's scheduled
 * flows); `hasScheduledTrigger` is what makes this flow relevant.
 */
export function FlowAuthorizationCard({ flow }: Readonly<{ flow: FlowResponse }>) {
  const { t } = useTranslation("workflows");
  const toast = useToast();
  const queryClient = useQueryClient();
  const capabilities = useCapabilities();
  const [consentOpen, setConsentOpen] = useState(false);

  const hasScheduledTrigger = (flow.triggers ?? []).some((tr) => tr.triggerType === "scheduled");
  const eligible = capabilities.automatedSyncEnabled && hasScheduledTrigger;

  const auth = useQuery({
    ...getFlowAuthorizationOptions({ path: { flow_uuid: flow.uuid } }),
    enabled: eligible,
  });

  const authorize = useMutation({
    ...authorizeFlowMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getFlowAuthorizationQueryKey({ path: { flow_uuid: flow.uuid } }),
      });
      setConsentOpen(false);
      toast.success(t("auth.authorized"));
    },
    onError: (e) => toast.error(errorMessage(e, t("auth.authorizeFailed"))),
  });

  // Hide entirely when not eligible, while loading, or when the feature is off.
  if (!eligible || auth.isPending) return null;
  const status = auth.data?.data.status;
  if (!status || status === "disabled") return null;

  const authorized = status === "authorized";
  const needsReauth = status === "needs_reauth";

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-2">
          <Shield size={16} aria-hidden className="mt-0.5 shrink-0 text-content-secondary" />
          <div className="flex min-w-0 flex-col gap-1">
            <Text variant="body">{t("auth.title")}</Text>
            <Text variant="body-sm" tone="tertiary">
              {authorized ? t("auth.authorizedBody") : t("auth.body")}
            </Text>
            {needsReauth && (
              <Text variant="caption" tone="warning">
                {t("auth.reauthNote")}
              </Text>
            )}
          </div>
        </div>
        <div className="shrink-0">
          {authorized ? (
            <Badge variant="success">{t("auth.authorized")}</Badge>
          ) : (
            <Button variant="primary" size="sm" onClick={() => setConsentOpen(true)}>
              {needsReauth ? t("auth.reauthorize") : t("auth.authorize")}
            </Button>
          )}
        </div>
      </div>

      <Modal
        open={consentOpen}
        onClose={() => setConsentOpen(false)}
        title={t("auth.consentTitle")}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConsentOpen(false)}>
              {t("triggers.cancel")}
            </Button>
            <Button
              variant="primary"
              loading={authorize.isPending}
              onClick={() => authorize.mutate({ path: { flow_uuid: flow.uuid } })}
            >
              {t("auth.consentAction")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-2">
          <Text variant="body-sm" tone="secondary">
            {t("auth.consentBody")}
          </Text>
          <Text variant="caption" tone="tertiary">
            {t("auth.consentNote")}
          </Text>
        </div>
      </Modal>
    </Card>
  );
}
